import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Calendar, MapPin, Users, ArrowRight, Loader2, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import usePageTitle from "@/hooks/usePageTitle";

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

function formatDate(d) {
  if (!d) return "";
  try { return new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "long", year: "numeric" }).format(new Date(d)); }
  catch { return d; }
}

export default function PublicEventsListPage() {
  usePageTitle("Evenements");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/public/events`);
        setEvents(res.data);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);

  const now = new Date();
  const upcoming = events.filter(e => new Date(e.event_date) >= now);
  const past = events.filter(e => new Date(e.event_date) < now);

  const filtered = (list) => {
    if (!search) return list;
    const q = search.toLowerCase();
    return list.filter(e =>
      e.title.toLowerCase().includes(q) ||
      (e.location || "").toLowerCase().includes(q) ||
      (e.org_name || "").toLowerCase().includes(q)
    );
  };

  return (
    <div className="min-h-screen bg-[#0B0F19]" data-testid="public-events-page">
      {/* Hero */}
      <div className="bg-gradient-to-b from-[#151B2B] to-[#0B0F19] py-16 px-4 text-center">
        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">Evenements</h1>
        <p className="text-[#94A3B8] max-w-lg mx-auto mb-8">
          Decouvrez les evenements a venir et inscrivez-vous en quelques secondes.
        </p>
        <div className="max-w-md mx-auto relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#64748b]" />
          <Input
            placeholder="Rechercher un evenement..."
            value={search} onChange={e => setSearch(e.target.value)}
            className="pl-10 bg-[#151B2B] border-[#2A3441] text-white placeholder:text-[#64748b]"
            data-testid="search-events"
          />
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 pb-16">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 text-[#F59E0B] animate-spin" /></div>
        ) : events.length === 0 ? (
          <div className="text-center py-16">
            <Calendar className="h-12 w-12 text-[#2A3441] mx-auto mb-4" />
            <p className="text-white font-bold text-lg">Aucun evenement pour le moment</p>
            <p className="text-[#64748b] mt-1">Revenez bientot !</p>
          </div>
        ) : (
          <>
            {/* Upcoming */}
            {filtered(upcoming).length > 0 && (
              <section className="mb-12">
                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#F59E0B]" /> A venir
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="upcoming-events-grid">
                  {filtered(upcoming).map(ev => <EventCard key={ev.id} ev={ev} />)}
                </div>
              </section>
            )}

            {/* Past (read-only) */}
            {filtered(past).length > 0 && (
              <section>
                <h2 className="text-lg font-bold text-[#64748b] mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#2A3441]" /> Termines
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 opacity-60" data-testid="past-events-grid">
                  {filtered(past).map(ev => <EventCard key={ev.id} ev={ev} past />)}
                </div>
              </section>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="text-center pb-8">
        <p className="text-[#64748b] text-xs">
          Propulse par <Link to="/" className="text-[#0099CC] hover:underline">GLOTECH DIGITAL</Link>
        </p>
      </div>
    </div>
  );
}

function EventCard({ ev, past }) {
  const url = `/e/${ev.org_slug}/${ev.slug}`;
  return (
    <Link to={url} className="group block bg-[#151B2B] border border-[#2A3441] rounded-xl overflow-hidden hover:border-[#F59E0B]/40 transition-colors" data-testid={`event-card-${ev.id}`}>
      {/* Cover */}
      {ev.cover_image_url ? (
        <div className="h-40 overflow-hidden">
          <img src={ev.cover_image_url} alt={ev.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        </div>
      ) : (
        <div className="h-40 bg-gradient-to-br from-[#F59E0B]/10 to-[#0B0F19] flex items-center justify-center">
          <Calendar className="h-10 w-10 text-[#F59E0B]/30" />
        </div>
      )}
      <div className="p-4 space-y-2">
        <p className="text-xs text-[#F59E0B] font-medium uppercase tracking-wider">{ev.org_name}</p>
        <h3 className="text-white font-bold text-base leading-tight group-hover:text-[#F59E0B] transition-colors">{ev.title}</h3>
        <div className="flex items-center gap-2 text-xs text-[#94A3B8]">
          <Calendar className="h-3.5 w-3.5" /> {formatDate(ev.event_date)}
        </div>
        {ev.location && (
          <div className="flex items-center gap-2 text-xs text-[#64748b]">
            <MapPin className="h-3.5 w-3.5" /> {ev.location}
          </div>
        )}
        {!past && (
          <div className="flex items-center justify-between pt-2">
            {ev.max_capacity > 0 && (
              <span className="text-xs text-[#64748b]">
                <Users className="h-3 w-3 inline mr-1" />{ev.total_registered || 0}/{ev.max_capacity}
              </span>
            )}
            <span className="text-[#F59E0B] text-xs font-bold flex items-center gap-1 ml-auto">
              S'inscrire <ArrowRight className="h-3 w-3" />
            </span>
          </div>
        )}
      </div>
    </Link>
  );
}
